import * as React from 'react'
import {
  FileText,
  Send,
  Eye,
  Clock,
  CircleDollarSign,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Calendar,
  Lock,
  Hash,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { Invoice } from '@/gen/cortado/v1/invoices_pb'
import { InvoiceStatusEnum, PaymentScheduleStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { formatCurrency } from '@/lib/currency-utils'
import { formatTimestamp, timestampToDateOnly } from '@/lib/date-utils'
import { getInvoiceStatusLabel } from '@/lib/enum-utils'
import { getCustomerFacingPaymentTotals } from '@/lib/invoice-payment-totals'
import { cn } from '@/lib/utils'
import { CopyButton } from '@/lib/clipboard-utils'

import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'

interface InvoiceSummaryCardProps {
  invoice: Invoice
}

/**
 * Invoice status configuration with colors and icons
 */
const invoiceStatusConfig: Record<
  number,
  {
    label: string
    badge: 'default' | 'success' | 'warning' | 'danger' | 'secondary' | 'info' | 'purple'
    accent: string
    accentDark: string
    icon: LucideIcon
  }
> = {
  [InvoiceStatusEnum.INVOICE_STATUS_DRAFT]: {
    label: 'Draft',
    badge: 'secondary',
    accent: 'bg-status-neutral-bg border-status-neutral-border text-status-neutral-fg',
    accentDark: '',
    icon: FileText,
  },
  [InvoiceStatusEnum.INVOICE_STATUS_SENT]: {
    label: 'Live in portal',
    badge: 'info',
    accent: 'bg-status-info-bg border-status-info-border text-status-info-fg',
    accentDark: '',
    icon: Send,
  },
  [InvoiceStatusEnum.INVOICE_STATUS_VIEWED]: {
    label: 'Viewed',
    badge: 'purple',
    accent: 'bg-purple-50 border-purple-200 text-purple-600',
    accentDark: 'dark:bg-purple-950/30 dark:border-purple-800 dark:text-purple-400',
    icon: Eye,
  },
  [InvoiceStatusEnum.INVOICE_STATUS_PAYMENT_PENDING]: {
    label: 'Payment Pending',
    badge: 'warning',
    accent: 'bg-status-warning-bg border-status-warning-border text-status-warning-fg',
    accentDark: '',
    icon: Clock,
  },
  [InvoiceStatusEnum.INVOICE_STATUS_PARTIALLY_PAID]: {
    label: 'Partially Paid',
    badge: 'warning',
    accent: 'bg-status-warning-bg border-status-warning-border text-status-warning-fg',
    accentDark: '',
    icon: CircleDollarSign,
  },
  [InvoiceStatusEnum.INVOICE_STATUS_PAID]: {
    label: 'Paid',
    badge: 'success',
    accent: 'bg-status-success-bg border-status-success-border text-status-success-fg',
    accentDark: '',
    icon: CheckCircle2,
  },
  [InvoiceStatusEnum.INVOICE_STATUS_OVERDUE]: {
    label: 'Overdue',
    badge: 'danger',
    accent: 'bg-status-danger-bg border-status-danger-border text-status-danger-fg',
    accentDark: '',
    icon: AlertTriangle,
  },
  [InvoiceStatusEnum.INVOICE_STATUS_CANCELLED]: {
    label: 'Cancelled',
    badge: 'secondary',
    accent: 'bg-status-neutral-bg border-status-neutral-border text-status-neutral-fg',
    accentDark: '',
    icon: XCircle,
  },
  [InvoiceStatusEnum.INVOICE_STATUS_OVERPAID]: {
    label: 'Overpaid',
    badge: 'success',
    accent: 'bg-status-success-bg border-status-success-border text-status-success-fg',
    accentDark: '',
    icon: CheckCircle2,
  },
}

function getStatusConfig(status: InvoiceStatusEnum) {
  return (
    invoiceStatusConfig[status] || {
      label: 'Unknown',
      badge: 'secondary' as const,
      accent: 'bg-status-neutral-bg border-status-neutral-border text-status-neutral-fg',
      accentDark: '',
      icon: FileText,
    }
  )
}

interface InfoRowProps {
  icon: LucideIcon
  label: string
  value: React.ReactNode
  extraClassName?: string
}

function InfoRow({ icon: Icon, label, value, extraClassName }: InfoRowProps) {
  return (
    <div className={cn('flex items-center gap-3', extraClassName)}>
      <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className="font-financial text-sm font-medium text-foreground truncate">{value}</div>
      </div>
    </div>
  )
}

export function InvoiceSummaryCard({ invoice }: InvoiceSummaryCardProps) {
  const statusConfig = getStatusConfig(invoice.status)
  const StatusIcon = statusConfig.icon

  // A payment schedule sits in PAYMENT_INITIATED once checkout completes but before
  // Stripe's settlement webhook lands (e.g. ACH transfers take 3-5 business days to clear).
  // This can happen on any invoice status, including PARTIALLY_PAID for deposit-plus-balance
  // invoices, so check schedules directly rather than gating on invoice status. The payment
  // method isn't available here, so the label stays payment-method-neutral.
  const isPaymentProcessing = React.useMemo(
    () =>
      invoice.paymentSchedules.some(
        (schedule) => schedule.status === PaymentScheduleStatusEnum.PAYMENT_SCHEDULE_STATUS_PAYMENT_INITIATED
      ),
    [invoice.paymentSchedules]
  )

  const statusLabel = isPaymentProcessing ? 'Payment processing' : getInvoiceStatusLabel(invoice.status)

  const totalCents = Number(invoice.totalCents || 0n)
  const tipCents = Number(invoice.tipCents || 0n)
  const goodwillRefundedCents = Number(invoice.goodwillRefundedCents || 0n)

  const isTipAccountingV1 = invoice.tipAccountingVersion >= 1
  const {
    paidCents,
    dueCents,
    tipPaidCents,
    tipDueCents,
  } = getCustomerFacingPaymentTotals(invoice)

  // Grand total includes tip for accurate progress calculation
  const grandTotalCents = totalCents + tipCents
  // Effective total is grand total minus goodwill refunds (which reduce what customer owes)
  const effectiveTotalCents = grandTotalCents - goodwillRefundedCents
  const hasTip = tipCents > 0
  const hasGoodwillRefund = goodwillRefundedCents > 0

  const progressPercentage = effectiveTotalCents > 0 ? Math.min((paidCents / effectiveTotalCents) * 100, 100) : 0
  const isPaid = paidCents >= effectiveTotalCents && effectiveTotalCents > 0

  // Find the earliest due date from payment schedules
  const nextDueDate = React.useMemo(() => {
    const unpaidSchedules = invoice.paymentSchedules
      .filter((s) => s.dueDate && Number(s.amountCents) > Number(s.amountPaidCents))
      .sort((a, b) => {
        const aTime = a.dueDate?.seconds || 0n
        const bTime = b.dueDate?.seconds || 0n
        return Number(aTime) - Number(bTime)
      })
    return unpaidSchedules[0]?.dueDate
  }, [invoice.paymentSchedules])

  return (
    <Card className="overflow-hidden pt-0 gap-0">
      {/* Status Header */}
      <CardHeader className={cn('py-4 border-b', statusConfig.accent, statusConfig.accentDark)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StatusIcon className="h-5 w-5" />
            <CardTitle className="text-base">{statusLabel}</CardTitle>
          </div>
          {invoice.isLocked && (
            <div className="flex items-center gap-1 text-xs opacity-70">
              <Lock className="h-3 w-3" />
              <span>Locked</span>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-3">
        {/* Invoice Number */}
        <div className="flex items-center gap-2">
          <Hash className="h-4 w-4 text-muted-foreground" />
          <span className="font-financial text-sm text-muted-foreground">
            {invoice.invoiceNumber || 'Draft'}
          </span>
          {invoice.invoiceNumber && (
            <CopyButton value={String(invoice.invoiceNumber)} ariaLabel="Copy invoice number" />
          )}
        </div>

        {/* Total Amount - Large Display */}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
            {hasGoodwillRefund ? 'Adjusted Total' : hasTip ? 'Grand Total' : 'Total'}
          </p>
          <p
            className={cn(
              'font-financial text-3xl font-bold tracking-tight',
              isPaid && 'text-status-success-fg'
            )}
          >
            {formatCurrency(effectiveTotalCents)}
          </p>
          {/* Breakdown when tip or goodwill refund exists */}
          {(hasTip || hasGoodwillRefund) && (
            <div className="text-xs space-y-0.5 mt-2 pt-2 border-t border-dashed">
              <div className="flex justify-between text-muted-foreground">
                <span>Invoice Total</span>
                <span className="font-financial">{formatCurrency(totalCents)}</span>
              </div>
              {hasTip && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Gratuity</span>
                  <span className="font-financial">+{formatCurrency(tipCents)}</span>
                </div>
              )}
              {hasGoodwillRefund && (
                <div className="flex justify-between text-status-success-fg">
                  <span>Goodwill Refund</span>
                  <span className="font-financial">-{formatCurrency(goodwillRefundedCents)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Payment Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Payment Progress</span>
            <span className="font-financial font-medium">{progressPercentage.toFixed(0)}%</span>
          </div>

          {/* Progress Bar */}
          <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500 ease-out',
                isPaid
                  ? 'bg-status-success-fg'
                  : progressPercentage > 0
                    ? 'bg-status-info-fg'
                    : 'bg-muted-foreground/20'
              )}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          {/* Paid / Due */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="rounded-lg border bg-muted/30 p-2.5 text-center">
              <p className="font-financial text-lg font-semibold text-status-success-fg">
                {formatCurrency(paidCents)}
              </p>
              <p className="text-xs text-muted-foreground">Paid</p>
              {isTipAccountingV1 && tipPaidCents > 0 && (
                <p className="font-financial text-[11px] text-muted-foreground">
                  incl. {formatCurrency(tipPaidCents)} tip
                </p>
              )}
            </div>
            <div
              className={cn(
                'rounded-lg border p-2.5 text-center',
                dueCents > 0
                  ? 'bg-status-warning-bg border-status-warning-border'
                  : 'bg-muted/30'
              )}
            >
              <p
                className={cn(
                  'font-financial text-lg font-semibold',
                  dueCents > 0 ? 'text-status-warning-fg' : 'text-muted-foreground'
                )}
              >
                {formatCurrency(dueCents)}
              </p>
              <p className="text-xs text-muted-foreground">Due</p>
              {isTipAccountingV1 && tipDueCents > 0 && (
                <p className="font-financial text-[11px] text-muted-foreground">
                  incl. {formatCurrency(tipDueCents)} tip
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Key Dates */}
        <div className="space-y-2.5 pt-2 border-t">
          {invoice.createdAt && (
            <InfoRow icon={Calendar} label="Created" value={formatTimestamp(invoice.createdAt)} />
          )}
          {invoice.sentAt && (
            <InfoRow icon={Send} label="Published" value={formatTimestamp(invoice.sentAt)} />
          )}
          {nextDueDate && (
            <InfoRow
              icon={Clock}
              label="Next Due"
              value={formatTimestamp(timestampToDateOnly(nextDueDate))}
              extraClassName={
                invoice.status === InvoiceStatusEnum.INVOICE_STATUS_OVERDUE
                  ? 'text-status-danger-fg'
                  : undefined
              }
            />
          )}
        </div>
      </CardContent>

      {/* Paid Celebration */}
      {isPaid && (
        <CardFooter className="border-t bg-status-success-bg py-3 justify-center">
          <div className="flex items-center gap-2 text-status-success-fg">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-medium">Fully Paid</span>
          </div>
        </CardFooter>
      )}
    </Card>
  )
}
